import * as THREE from 'three';
import type { WeaponId } from '../weapons/WeaponDefs';
import {
  applyStance,
  bakedPunchClip,
  blendStance,
  punchPlayDuration,
  samplePunchClip,
  type PunchClipId,
} from './PunchClip';
import { ProcArms } from './ProcArms';
import { buildProcPickup } from './ProcWeapons';
import {
  createJacketBridges,
  flattenHoldMaterials,
  hideBodyArms,
  placeHoldEye,
  updateJacketBridges,
} from './FpsHoldBridges';

/** Same proc-mode hip/ADS numbers as `Viewmodel.poseFor`. */
function procCameraPose(id: WeaponId): {
  hipPos: THREE.Vector3;
  hipRot: THREE.Euler;
  adsPos: THREE.Vector3;
  adsRot: THREE.Euler;
} {
  switch (id) {
    case 'pistol':
      return {
        hipPos: new THREE.Vector3(0.065, -0.125, -0.51),
        hipRot: new THREE.Euler(0.08, 0.05, 0.02),
        adsPos: new THREE.Vector3(0.0, -0.042, -0.393),
        adsRot: new THREE.Euler(0.0, 0.0, 0.0),
      };
    case 'shotgun':
      return {
        hipPos: new THREE.Vector3(0.085, -0.135, -0.43),
        hipRot: new THREE.Euler(0.1, 0.04, 0.02),
        adsPos: new THREE.Vector3(0.0, -0.04, -0.24),
        adsRot: new THREE.Euler(-0.02, 0.0, 0.0),
      };
    case 'sawedoff':
      return {
        hipPos: new THREE.Vector3(0.07, -0.11, -0.42),
        hipRot: new THREE.Euler(0.08, 0.03, 0.01),
        adsPos: new THREE.Vector3(0.0, -0.042, -0.28),
        adsRot: new THREE.Euler(-0.03, 0.0, 0.0),
      };
    case 'mp5':
      return {
        hipPos: new THREE.Vector3(0.055, -0.115, -0.3),
        hipRot: new THREE.Euler(0.06, 0.03, -0.02),
        adsPos: new THREE.Vector3(0.0, -0.058, -0.22),
        adsRot: new THREE.Euler(0.0, 0.0, 0.0),
      };
    case 'ak47':
      return {
        hipPos: new THREE.Vector3(0.055, -0.115, -0.3),
        hipRot: new THREE.Euler(0.06, 0.03, -0.02),
        adsPos: new THREE.Vector3(0.0, -0.05, -0.22),
        adsRot: new THREE.Euler(0.0, 0.0, 0.0),
      };
    case 'punch':
      return {
        hipPos: new THREE.Vector3(0, -0.02, 0),
        hipRot: new THREE.Euler(0, 0, 0),
        adsPos: new THREE.Vector3(0, 0, 0),
        adsRot: new THREE.Euler(0, 0, 0),
      };
    default:
      return {
        hipPos: new THREE.Vector3(0.065, -0.125, -0.28),
        hipRot: new THREE.Euler(0.08, 0.05, 0.02),
        adsPos: new THREE.Vector3(0.0, -0.042, -0.22),
        adsRot: new THREE.Euler(0.0, 0.0, 0.0),
      };
  }
}

export type FpsWorldHold = {
  eye: THREE.Group;
  hold: THREE.Group;
  pose: THREE.Group;
  weapon: THREE.Object3D;
  weaponId: WeaponId;
  gripR: THREE.Object3D | null;
  gripL: THREE.Object3D | null;
  arms: ProcArms;
  upperArmR: THREE.Mesh;
  upperArmL: THREE.Mesh;
  shoulderR: THREE.Mesh;
  shoulderL: THREE.Mesh;
  ads: number;
};

export function createFpsWorldHold(
  weaponId: WeaponId,
  sleeveHex: number,
  skinCss: string,
): FpsWorldHold {
  const skin = new THREE.MeshBasicMaterial({ color: skinCss, fog: true });
  const sleeve = new THREE.MeshBasicMaterial({ color: sleeveHex, fog: true });
  const nail = new THREE.MeshBasicMaterial({
    color: 0xf3c8be,
    fog: true,
    transparent: true,
    opacity: 0.72,
  });
  const arms = new ProcArms({ skin, sleeve, nail });
  arms.useWeapon(weaponId);

  const eye = new THREE.Group();
  eye.name = 'fpsHoldEye';
  const hold = new THREE.Group();
  hold.name = 'fpsWorldHold';
  const pose = new THREE.Group();
  pose.name = 'fpsWorldPose';
  const weaponSlot = new THREE.Group();
  weaponSlot.name = 'fpsWeaponSlot';
  const weapon = buildProcPickup(weaponId);
  weapon.name = weapon.name || `fpsWeapon_${weaponId}`;
  weaponSlot.add(weapon);
  pose.add(weaponSlot);
  pose.add(arms.root);
  hold.add(pose);
  eye.add(hold);

  flattenHoldMaterials(hold);

  const bridges = createJacketBridges(sleeveHex);
  const gripR = weapon.getObjectByName('grip_r') ?? null;
  const gripL = weapon.getObjectByName('grip_l') ?? null;

  return {
    eye,
    hold,
    pose,
    weapon,
    weaponId,
    gripR,
    gripL,
    arms,
    ...bridges,
    ads: 0,
  };
}

export function attachFpsWorldHold(parent: THREE.Object3D, h: FpsWorldHold, enemyId: number): void {
  parent.add(h.eye);
  parent.add(h.upperArmR);
  parent.add(h.upperArmL);
  parent.add(h.shoulderR);
  parent.add(h.shoulderL);
  const tag = (o: THREE.Object3D) => {
    o.userData.enemyId = enemyId;
    o.userData.enemyHit = true;
  };
  h.eye.traverse(tag);
  for (const m of [h.upperArmR, h.upperArmL, h.shoulderR, h.shoulderL]) tag(m);
}

function posePunchGrips(
  h: FpsWorldHold,
  punch: { id: PunchClipId; u: number } | 'guard' | 'rest',
): void {
  if (!h.gripR || !h.gripL) return;
  const ads = punch === 'rest' ? 0 : 1;
  const fists =
    punch === 'guard' || punch === 'rest'
      ? blendStance(ads)
      : applyStance(samplePunchClip(bakedPunchClip(punch.id), punch.u), ads);
  h.gripR.position.set(fists.right.x, fists.right.y, fists.right.z);
  h.gripR.rotation.set(fists.right.rx, fists.right.ry, fists.right.rz);
  h.gripL.position.set(fists.left.x, fists.left.y, fists.left.z);
  h.gripL.rotation.set(fists.left.rx, fists.left.ry, fists.left.rz);
}

export function poseFpsWorldHold(
  h: FpsWorldHold,
  ads: number,
  punch: { id: PunchClipId; u: number } | 'guard' | 'rest' | null,
): void {
  h.ads = ads;
  const { hipPos, hipRot, adsPos, adsRot } = procCameraPose(h.weaponId);
  h.pose.position.lerpVectors(hipPos, adsPos, ads);
  h.pose.rotation.order = 'YXZ';
  h.pose.rotation.set(
    THREE.MathUtils.lerp(hipRot.x, adsRot.x, ads),
    THREE.MathUtils.lerp(hipRot.y, adsRot.y, ads),
    THREE.MathUtils.lerp(hipRot.z, adsRot.z, ads),
  );
  if (h.weaponId === 'punch' && punch) posePunchGrips(h, punch);
  h.arms.update(h.gripR, h.gripL, 0, 0, ads);
}

export function setFpsWorldHoldVisible(h: FpsWorldHold, visible: boolean): void {
  h.eye.visible = visible;
  if (!visible) {
    h.upperArmR.visible = false;
    h.upperArmL.visible = false;
    h.shoulderR.visible = false;
    h.shoulderL.visible = false;
  }
}

export function syncFpsWorldHold(
  h: FpsWorldHold,
  opts: {
    visible: boolean;
    ads: number;
    punch: { id: PunchClipId; u: number } | 'guard' | 'rest' | null;
    lookPitch: number;
    kneelBlend: number;
    bones: Record<string, THREE.Object3D> | undefined;
    parent: THREE.Object3D;
  },
): void {
  hideBodyArms(opts.bones, opts.visible);
  setFpsWorldHoldVisible(h, opts.visible);
  if (!opts.visible) return;
  placeHoldEye(h.eye, opts.ads, h.weaponId === 'punch', opts.lookPitch, opts.kneelBlend);
  poseFpsWorldHold(h, opts.ads, opts.punch);
  updateJacketBridges(
    h.hold,
    opts.bones,
    opts.parent,
    h.upperArmR,
    h.upperArmL,
    h.shoulderR,
    h.shoulderL,
  );
}

export function punchProgress(id: PunchClipId, attackLockT: number, phase: string): number {
  const clip = bakedPunchClip(id);
  const dur = punchPlayDuration(clip);
  if (phase === 'windup') return clip.keys.find((k) => k.id === 'pos1')?.t ?? 0.2;
  return 1 - Math.min(1, attackLockT / Math.max(0.05, dur));
}
